'use client';

/**
 * ui-future primitives — Component Sheet is the spec.
 * Every primitive uses tokens from theme/tokens.css; nothing global.
 */
import { useState } from 'react';

export const mono = (size = 17, tracking = '.3em'): React.CSSProperties =>
  ({ fontFamily: 'var(--uif-font-mono)', fontSize: size, letterSpacing: tracking, textTransform: 'uppercase' as const });
export const display = (size = 52, weight = 200): React.CSSProperties =>
  ({ fontFamily: 'var(--uif-font-body)', fontSize: size, fontWeight: weight });

export function Glass({ children, className = '', style, focused }: {
  children: React.ReactNode; className?: string; style?: React.CSSProperties; focused?: boolean;
}) {
  return (
    <div className={`relative ${className}`} style={{
      background: 'var(--uif-glass-bg)',
      border: `1px solid ${focused ? 'var(--uif-glass-border-focus)' : 'var(--uif-glass-border)'}`,
      borderRadius: 'var(--uif-radius-lg)',
      boxShadow: 'var(--uif-glass-glow)',
      ...style,
    }}>{children}</div>
  );
}

export function Pill({ label, active, onClick }: { label: string; active?: boolean; onClick?: () => void }) {
  return (
    <button onMouseUp={onClick} className="px-3 py-1 rounded-full transition-all" style={{
      ...mono(16, '.22em'),
      background: active ? 'var(--uif-pill-active)' : 'transparent',
      color: active ? 'var(--uif-pill-active-text)' : 'var(--uif-t4)',
      border: active ? '1px solid transparent' : '1px solid rgba(125,232,255,.2)',
    }}>{label}</button>
  );
}

export function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className="px-2 py-0.5 rounded" style={{
      ...mono(15, '.14em'), color, border: `1px solid ${color}44`, background: `${color}14`,
    }}>{label}</span>
  );
}

export function StatusChip({ label, kind }: { label: string; kind: 'good' | 'warn' | 'bad' | 'info' | 'dim' }) {
  const c = kind === 'good' ? 'var(--uif-good)' : kind === 'warn' ? 'var(--uif-warn)' : kind === 'bad' ? 'var(--uif-bad)' : kind === 'info' ? 'var(--uif-info)' : 'var(--uif-t5)';
  return <span className="px-2 py-0.5 rounded-full" style={{ ...mono(15, '.12em'), color: c, border: `1px solid ${c}` }}>{label}</span>;
}

export function Delta({ value }: { value: number | null | undefined }) {
  if (value == null) return <span style={{ ...mono(15), color: 'var(--uif-t6)' }}>—</span>;
  const up = value >= 0;
  return (
    <span style={{ ...mono(15, '.08em'), color: up ? 'var(--uif-good)' : 'var(--uif-bad)' }}>
      {up ? '▲' : '▼'} {Math.abs(value)}%
    </span>
  );
}

export function Kpi({ label, value, sub, onClick }: { label: string; value: string | number; sub?: string; onClick?: () => void }) {
  return (
    <Glass className="px-5 py-4 min-w-[150px] cursor-none" style={{ borderRadius: 'var(--uif-radius)' }}>
      <button onMouseUp={onClick} className="text-left w-full">
        <div style={{ ...mono(15, '.3em'), color: 'var(--uif-t5)' }}>{label}</div>
        <div className="uif-pulse mt-1.5" style={{ ...display(60, 200), color: 'var(--uif-cy-numeral)' }}>{value}</div>
        {sub && <div className="mt-1" style={{ ...mono(15, '.12em'), color: 'var(--uif-t5)' }}>{sub}</div>}
      </button>
    </Glass>
  );
}

/** Execute state trio: idle → EXECUTING… (amber pulse) → ✓ DONE (green) */
export function ExecuteBtn({ label, onRun }: { label: string; onRun?: () => Promise<void> | void }) {
  const [state, setState] = useState<'idle' | 'run' | 'done'>('idle');
  return (
    <button
      onMouseUp={async () => {
        if (state !== 'idle') return;
        setState('run');
        try { await onRun?.(); } catch {}
        setState('done');
        setTimeout(() => setState('idle'), 2400);
      }}
      className="px-3 py-1.5 rounded-full transition-all"
      style={{
        ...mono(16, '.18em'),
        color: state === 'done' ? 'var(--uif-good)' : state === 'run' ? 'var(--uif-warn)' : 'var(--uif-pill-active-text)',
        background: state === 'idle' ? 'var(--uif-pill-active)' : 'transparent',
        border: state === 'idle' ? '1px solid transparent' : `1px solid ${state === 'done' ? 'var(--uif-good)' : 'var(--uif-warn)'}`,
        animation: state === 'run' ? 'uifBreathe 1.2s ease-in-out infinite' : undefined,
      }}
    >
      {state === 'idle' ? `▶ ${label}` : state === 'run' ? 'EXECUTING…' : '✓ DONE'}
    </button>
  );
}

/** Honest empty state: dashed ring + NO [X] YET + next action */
/** Prototype status palette — Control Room v4 `sc` verbatim. */
export const SC = { amber: '#ffc36b', cyan: '#9ef0ff', green: '#5df2b4', red: '#ff7d8a', dim: '#7fa9bd' } as const;

/**
 * SpecCard — the round-1 "cards mode" card, styles verbatim from
 * Control Room v4 tvCards (VIDEO LAB · A/B TESTS · WEBSITES · PEOPLE).
 * width 336 · radius 14 · pad 16 · gradient glass · top hairline in badge hue ·
 * big 30/200 #aef0ff w/ cyan glow · delta mono 8.5 (↓ → red) · 2px glow bar · badge pill hex66.
 */
export function SpecCard({ title, sub, big, delta, w, foot, badge, badgeHex, onTap, href }: {
  title: string; sub: string; big: string; delta?: string; w?: number; foot?: string;
  badge?: string; badgeHex?: string; onTap?: () => void; href?: string;
}) {
  const hex = badgeHex || '#56788c';
  const body = (
    <div style={{
      width: 336, boxSizing: 'border-box', position: 'relative', borderRadius: 14, padding: 16,
      background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.5) 60%)',
      border: '1px solid rgba(125,232,255,.2)', overflow: 'hidden',
      transition: 'border-color .25s,transform .25s', cursor: onTap || href ? 'pointer' : undefined,
    }}>
      <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1,
        background: `linear-gradient(90deg,transparent,${hex},transparent)` }} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <div style={{ ...display(26, 300), color: 'var(--uif-t1)' }}>{title}</div>
          <div className="mt-1" style={{ fontFamily: 'var(--uif-font-mono)', fontSize: 15, letterSpacing: '.14em', color: '#56788c', whiteSpace: 'nowrap' }}>{sub}</div>
        </div>
        {badge && (
          <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${hex}66`, color: hex,
            fontFamily: 'var(--uif-font-mono)', fontSize: 14, letterSpacing: '.14em', whiteSpace: 'nowrap' }}>{badge}</span>
        )}
      </div>
      <div className="mt-4" style={{ fontWeight: 200, fontSize: 60, color: '#aef0ff', textShadow: '0 0 16px rgba(0,240,255,.35)', lineHeight: 1, fontFamily: 'var(--uif-font-body)' }}>{big}</div>
      {delta != null && (
        <div className="mt-1" style={{ fontFamily: 'var(--uif-font-mono)', fontSize: 17,
          color: delta.indexOf('↓') === 0 ? '#ff7d8a' : '#7fa9bd', letterSpacing: '.08em' }}>{delta}</div>
      )}
      <div className="mt-3" style={{ height: 2, width: `${w || 0}%`,
        background: `linear-gradient(90deg,${badgeHex || '#9ef0ff'},rgba(125,232,255,.06))`,
        boxShadow: `0 0 8px ${badgeHex || '#9ef0ff'}55` }} />
      {foot && <div className="mt-3" style={{ fontFamily: 'var(--uif-font-mono)', fontSize: 15, letterSpacing: '.14em', color: '#7fa9bd', whiteSpace: 'nowrap' }}>{foot}</div>}
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>{body}</a>;
  if (onTap) return <div onMouseUp={onTap}>{body}</div>;
  return body;
}

export function EmptyState({ what, action }: { what: string; action?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="w-16 h-16 rounded-full border border-dashed flex items-center justify-center"
        style={{ borderColor: 'rgba(125,232,255,.3)' }}>
        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--uif-t6)' }} />
      </div>
      <div style={{ ...mono(18, '.3em'), color: 'var(--uif-t4)' }}>NO {what.toUpperCase()} YET</div>
      {action && <div style={{ ...mono(16, '.14em'), color: 'var(--uif-t6)' }}>{action}</div>}
    </div>
  );
}

/** Holo table shell — 52px rows, hairline dividers */
export function HoloTable({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="w-full">
      <div className="grid gap-3 px-4 pb-2" style={{ gridTemplateColumns: `var(--uif-cols, repeat(${head.length}, 1fr))`, borderBottom: '1px solid rgba(125,232,255,.14)' }}>
        {head.map(h => <div key={h} style={{ ...mono(15, '.24em'), color: 'var(--uif-t5)' }}>{h}</div>)}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function Row({ children, onOpen, cols }: { children: React.ReactNode; onOpen?: () => void; cols: number }) {
  return (
    <button onMouseUp={onOpen}
      className="grid gap-3 px-4 items-center w-full text-left transition-colors hover:bg-white/[0.04]"
      style={{ gridTemplateColumns: `var(--uif-cols, repeat(${cols}, 1fr))`, height: 'var(--uif-row-h)', borderBottom: '1px solid rgba(125,232,255,.07)' }}>
      {children}
    </button>
  );
}
